/**
 * Страница личного кабинета (заглушка)
 * 
 * Временная заглушка для страницы личного кабинета.
 * Отображает сообщение о том, что личный кабинет находится в разработке,
 * и предоставляет кнопку для возврата на главную страницу.
 */

import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { styled, ThemeProvider, createTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
// @ts-ignore
import logotype from '@app/assets/ok-telecom-black.png';
import { Container } from '@mui/system';

// Розоватая тема как в web-app
const pinkTheme = createTheme({
  palette: {
    primary: {
      main: '#e91e63', // Розовый цвет
      light: '#f48fb1',
      dark: '#c2185b',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e1e1e',
      secondary: '#757575',
    },
  },
  typography: {
    fontFamily: '"Ubuntu", "Helvetica", "Roboto", sans-serif',
  },
});

// Контейнер всей страницы
const PageWrapper = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  width: '100%',
  backgroundColor: '#ffffff',

  // Адаптивность: на мобильных устройствах колонки идут вертикально
  '@media (max-width: 900px)': {
    flexDirection: 'column',
  },
});

// Левая часть с логотипом (1/4 ширины)
const LogoSection = styled(Box)(({ theme }) => ({
  flex: '0 0 25%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  position: 'relative',

  // Адаптивность: на планшетах и мобильных
  '@media (max-width: 900px)': {
    flex: '0 0 auto',
    padding: theme.spacing(3),
    minHeight: '150px',
  },

  '@media (max-width: 600px)': {
    padding: theme.spacing(2),
    minHeight: '120px',
  },
}));

// Вертикальный разделитель
const Divider = styled(Box)({
  width: '1px',
  backgroundColor: '#e0e0e0',

  // Адаптивность: на мобильных становится горизонтальным
  '@media (max-width: 900px)': {
    width: '100%',
    height: '1px',
  },
});

// Правая часть с контентом (3/4 ширины)
const ContentSection = styled(Box)(({ theme }) => ({
  flex: '0 0 75%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(6),
  textAlign: 'center',

  // Адаптивность
  '@media (max-width: 900px)': {
    flex: '1',
    padding: theme.spacing(4),
  },

  '@media (max-width: 600px)': {
    padding: theme.spacing(3),
  },
}));

// Логотип (вдвое меньше)
const LogoImage = styled('img')({
  maxWidth: '100px',
  width: '100%',
  height: 'auto',

  '@media (max-width: 900px)': {
    maxWidth: '75px',
  },

  '@media (max-width: 600px)': {
    maxWidth: '60px',
  },
});

// Контейнер контента
const ContentContainer = styled(Box)({
  maxWidth: '600px',
  width: '100%',
});



const Title = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 600,
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,

  '@media (max-width: 600px)': {
    fontSize: '1.5rem',
  },
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(4),

  '@media (max-width: 600px)': {
    fontSize: '1rem',
    marginBottom: theme.spacing(3),
  },
}));

// Кнопка в стиле RedButton с главной страницы
const HomeButton = styled(Button)({
  backgroundColor: '#ff0057 !important',
  color: 'white !important',
  boxShadow: '0 4px 6px rgba(50, 50, 93, 0.11), 0 1px 3px rgba(0, 0, 0, 0.08)',
  padding: '14px !important',
  fontSize: '14px !important',
  textTransform: 'none',
  position: 'relative',
  zIndex: 1,
  cursor: 'pointer',
  pointerEvents: 'auto',

  '&:hover': {
    backgroundColor: '#ff0000 !important',
    color: 'white !important',
  },

  '@media (max-width: 600px)': {
    padding: '10px 12px !important',
    fontSize: '13px !important',
    minWidth: 'auto !important',
  },
});

/**
 * Страница личного кабинета (заглушка)
 * 
 * Временная страница-заглушка для личного кабинета.
 * Отображает:
 * - Логотип OK-Telecom слева (1/4 ширины)
 * - Вертикальный разделитель
 * - Контент справа (3/4 ширины):
 *   - Заголовок "Личный кабинет"
 *   - Сообщение о том, что функционал находится в разработке
 *   - Кнопку "Вернуться на главную" для перехода на лендинг
 * 
 * Адаптивна для мобильных устройств:
 * - Desktop: логотип слева (25%), контент справа (75%)
 * - Tablet/Mobile: логотип сверху, контент снизу (вертикальная компоновка)
 * 
 * @example
 * ```tsx
 * // В роутере
 * <Route path="/lk" element={<LkPage />} />
 * ```
 */
export const LkPage: React.FC = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <ThemeProvider theme={pinkTheme}>
      <Container maxWidth='md'>
        <PageWrapper>
          {/* Левая часть с логотипом */}
          <LogoSection>
            <LogoImage src={logotype} alt="OK-Telecom" />
          </LogoSection>

          {/* Вертикальный разделитель */}
          <Divider />

          {/* Правая часть с контентом */}
          <ContentSection>
            <ContentContainer>
              <Title variant="h4">
                Личный кабинет
              </Title>

              <Description variant="body1">
                Личный кабинет находится в разработке.
                Скоро здесь вы сможете управлять своими услугами,
                просматривать историю платежей и изменять настройки.
              </Description>

              <HomeButton
                variant="contained"
                size="large"
                onClick={handleGoHome}
              >
                Вернуться на главную
              </HomeButton>
            </ContentContainer>
          </ContentSection>
        </PageWrapper>
      </Container>
    </ThemeProvider>
  );
};
